import * as React from 'react';
import DropdownCommentsModal, { BaseDropdownModalProps } from '../DropdownCommentsModal/DropdownCommentsModal';
import { CoughReOpenCommentGroup, ModelWithError } from '../../../../misc/api/apiTypes';
import { IsFreeTextAllowed, GetSelectedOptions, DropdownCommentTypes, ValidateSubmittedComments, ShouldDisplay } from '../DropdownCommentsModal/DropdownCommentsHelpers';
import { connect } from 'react-redux';
import { MainState } from '../../../../Core/reducers';
import { AuditModal } from '../../../../auditmodal/components/AuditModal';
import { useWebPortalLegacyClient } from '../../../../misc/api/WebPortalTokenClient';

interface ReviewCompleteCommentsModalProps extends BaseDropdownModalProps, ReviewCompleteCommentsModalOwnProps {
}

interface ReviewCompleteCommentsModalOwnProps {
    onClose: () => void;
    isAnalysisSetLinked: boolean;
    reOpenedDuringReview: boolean;
    selectedDropdownCommentType: DropdownCommentTypes;
    analysisSetId: string;
}

function ValidateCreateComments(selectedFixedComments: CoughReOpenCommentGroup[], auditAnnotation: string, analysisSetWasReopened: boolean): string[] {

    const errors = [];

    if (!analysisSetWasReopened && IsFreeTextAllowed(selectedFixedComments) && !auditAnnotation.trim()) {
        errors.push("Marking an analysis set as review complete and not re-opened requires a free text comment.");
    }

    if (analysisSetWasReopened && !auditAnnotation.trim()) {
        errors.push("Marking an analysis set as review complete that was re-opened requires a text comment.");
    }

    return errors;
}

const ReviewCompleteCommentsModal = ({ analysisSetId, selectedDropdownCommentType, onClose, reOpenedDuringReview, ...props }: ReviewCompleteCommentsModalProps): JSX.Element => {

    const [loading, setLoading] = React.useState<boolean>(false);
    const [err, setErr] = React.useState<string>();
    const legacyClient = useWebPortalLegacyClient();

    const saveFixedComments = (selectedComments: CoughReOpenCommentGroup[], auditAnnotation: string) => {
        return legacyClient.markAnalysisSetAsReviewComplete(analysisSetId, auditAnnotation, GetSelectedOptions(selectedComments));
    }

    const reviewCompleteCommentValidator = (selectedComments: CoughReOpenCommentGroup[], auditAnnotation: string): string[] => {
        return ValidateSubmittedComments(selectedComments).concat(ValidateCreateComments(selectedComments, auditAnnotation, reOpenedDuringReview));
    }

    const shouldDisplay = ShouldDisplay(DropdownCommentTypes.ReviewComplete);

    const getElement = () => {

        if (!shouldDisplay(analysisSetId, selectedDropdownCommentType)) {
            return null;
        }

        if (reOpenedDuringReview) {
            return (<AuditModal isSubmitting={loading} error={err} header={"Analysis Set Review Complete"} onClose={onClose} onSubmit={(form) => {
                const errors = ValidateCreateComments([], form.Annotation, reOpenedDuringReview);

                if (errors.length) {
                    setErr(errors[0]);
                    return;
                }

                setLoading(true);
                saveFixedComments([], form.Annotation)
                    .catch((err: ModelWithError) => {
                        setErr(err.Errors[0].Message);
                    })
                    .finally(() => {
                        setLoading(false);
                        setErr(null);
                        onClose();
                    });
            }} />);
        } else {
            return (<DropdownCommentsModal loading={loading} editMode={false} fetchFixedCommentsForAnalysisSet={() => Promise.resolve(null)} header="Analysis Set Review Complete" showLinkedWarning={false} saveFixedComments={saveFixedComments} show={selectedDropdownCommentType === DropdownCommentTypes.ReviewComplete} selectedCommentsValidator={reviewCompleteCommentValidator} onClose={onClose} {...props} />);
        }

    };

    return (getElement());
};

function mapStateToProps(state: MainState, ownProps: ReviewCompleteCommentsModalOwnProps): ReviewCompleteCommentsModalProps {
    return {
        analysisSetId: ownProps.analysisSetId,
        isAnalysisSetLinked: ownProps.isAnalysisSetLinked,
        reOpenedDuringReview: ownProps.reOpenedDuringReview,
        selectedDropdownCommentType: ownProps.selectedDropdownCommentType,
        onClose: ownProps.onClose
    };
}

export default connect<ReviewCompleteCommentsModalProps, void, ReviewCompleteCommentsModalOwnProps>(mapStateToProps)(ReviewCompleteCommentsModal);
